import React, { useState, useEffect } from "react";
import PageHeader from "../components/PageHeader";
import { createComplaint } from "../services/complaintService";
import jsPDF from "jspdf";

export default function RegisterComplaint() {
  const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB
  const MAX_AADHAAR_FILE_SIZE = 2 * 1024 * 1024; // 2MB
  const ALLOWED_FILE_EXTENSIONS = ["pdf", "jpg", "jpeg", "png", "mp4", "mpeg", "mp3", "wav"];
  const ALLOWED_AADHAAR_EXTENSIONS = ["pdf", "jpg", "jpeg", "png"];

  const getFileExtension = (fileName = "") => {
    const parts = fileName.toLowerCase().split(".");
    return parts.length > 1 ? parts.pop() : "";
  };

  const isValidFileByExtension = (file, allowedExtensions) => {
    const ext = getFileExtension(file?.name || "");
    return allowedExtensions.includes(ext);
  };

  const [formData, setFormData] = useState({
    complainantName: "",
    email: "",
    mobile: "",
    adharNumber: "",
    victimGender: "",
    victimAge: "",
    relationWithVictim: "",
    state: "",
    district: "",
    address: "",
    complaintHead: "",
    complaintDetails: "",
    caseRegistered: "No",
    casePending: "No",
    oathAccepted: false,
    captcha: "",
  });

  const [socialLinks, setSocialLinks] = useState([]);
  const [filesList, setFilesList] = useState([]);
  const [aadhaarFile, setAadhaarFile] = useState(null);
  const [status, setStatus] = useState("");
  const [generatedCaptcha, setGeneratedCaptcha] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [applicationId, setApplicationId] = useState("");
  const [districtsByState, setDistrictsByState] = useState({});
  const [districtsError, setDistrictsError] = useState("");
  const [submittedSnapshot, setSubmittedSnapshot] = useState(null);
  const [submittedAt, setSubmittedAt] = useState("");

  const breadcrumbs = [
    { label: "Home", link: "/" },
    { label: "Register Complaint", active: true },
  ];

  // Generate Captcha
  const generateCaptcha = () => {
    const chars = "0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ";
    let captcha = "";
    for (let i = 0; i < 6; i++) captcha += chars[Math.floor(Math.random() * chars.length)];
    setGeneratedCaptcha(captcha);
  };

  useEffect(() => generateCaptcha(), []);

  useEffect(() => {
    const normalize = (value) =>
      String(value || "")
        .toLowerCase()
        .replace(/and/g, "")
        .replace(/&/g, "")
        .replace(/[^a-z]/g, "");

    const loadDistricts = async () => {
      try {
        setDistrictsError("");
        const res = await fetch(
          `${process.env.PUBLIC_URL}/assests/data/india-states-and-districts.json`
        );
        const data = await res.json();
        const map = {};
        (data.states || []).forEach((stateObj) => {
          const key = normalize(stateObj.state);
          map[key] = (stateObj.districts || []).map((d) => d.name);
        });
        setDistrictsByState(map);
      } catch (err) {
        console.error("Failed to load districts list", err);
        setDistrictsError("District list failed to load. You can type the district manually.");
      }
    };

    loadDistricts();
  }, []);

  // Handle form input
  const handleChange = (e) => {
    const { id, value, type, checked } = e.target;
    if (id === "state") {
      setFormData({ ...formData, [id]: value, district: "" });
      return;
    }
    if (type === "checkbox") {
      setFormData({ ...formData, [id]: checked });
      return;
    }
    setFormData({ ...formData, [id]: value });
  };

  const handleRadioChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Social Media Links
  const addSocialLink = () => setSocialLinks([...socialLinks, { link: "" }]);
  const removeSocialLink = (index) => setSocialLinks(socialLinks.filter((_, i) => i !== index));
  const handleSocialLinkChange = (index, value) => {
    const updated = [...socialLinks];
    updated[index].link = value;
    setSocialLinks(updated);
  };
  // File Uploads
  const addFileInput = () => setFilesList([...filesList, null]);
  const handleFileChange = (index, e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    if (!isValidFileByExtension(selectedFile, ALLOWED_FILE_EXTENSIONS)) {
      alert("Only pdf, jpg, jpeg, png, mp4, mpeg, mp3, wav files are allowed.");
      e.target.value = "";
      return;
    }

    if (selectedFile.size > MAX_FILE_SIZE) {
      alert("Maximum file size should be 20MB.");
      e.target.value = "";
      return;
    }

    const updated = [...filesList];
    updated[index] = selectedFile;
    setFilesList(updated);
  };
  const removeFileInput = (index) => setFilesList(filesList.filter((_, i) => i !== index));

  const handleAadhaarFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) {
      setAadhaarFile(null);
      return;
    }

    if (!isValidFileByExtension(selectedFile, ALLOWED_AADHAAR_EXTENSIONS)) {
      alert("Aadhaar file must be pdf, jpg, jpeg or png.");
      e.target.value = "";
      setAadhaarFile(null);
      return;
    }

    if (selectedFile.size > MAX_AADHAAR_FILE_SIZE) {
      alert("Maximum Aadhaar file size should be 2MB.");
      e.target.value = "";
      setAadhaarFile(null);
      return;
    }

    setAadhaarFile(selectedFile);
  };

  const loadImageDataUrl = (src) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      };
      img.onerror = reject;
      img.src = src;
    });

  const formatDateTime = (value) => {
    if (!value) return "-";
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return String(value);
    return dt.toLocaleString("en-IN", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  // PDF Generator
  const generatePDF = async () => {
    const doc = new jsPDF();
    let y = 15;

    const LETTERHEAD = `${process.env.PUBLIC_URL}/assests/img/bsc-letterhead.jpg`;

    try {
      const letterhead = await loadImageDataUrl(LETTERHEAD);
      doc.addImage(letterhead, "JPEG", 0, 0, 210, 297);
    } catch (err) {
      console.error("Letterhead failed to load", err);
    }

    // Start content below header area
    y = 70;

    const labelColor = [70, 70, 70];
    const valueColor = [20, 20, 20];
    const col1X = 16;
    const col2X = 110;
    const rowGap = 8;

    const fieldRow = (x, label, value) => {
      doc.setFontSize(10);
      doc.setTextColor(...labelColor);
      doc.text(`${label}:`, x, y);
      doc.setTextColor(...valueColor);
      const lines = doc.splitTextToSize(String(value || "-"), 70);
      doc.text(lines, x + 34, y);
    };

    doc.setFontSize(13);
    doc.setTextColor(10);
    doc.text("Complaint Details Online", 105, y, { align: "center" });
    y += 10;

    const dataSource = submittedSnapshot || formData;
    const socialSource = submittedSnapshot?.socialLinks || socialLinks;
    const filesCountSource = submittedSnapshot?.filesCount ?? filesList.filter(Boolean).length;

    fieldRow(col1X, "Complaint ID", applicationId || "NA");
    fieldRow(
      col2X,
      "Complainant Date",
      formatDateTime(submittedAt || dataSource?.createdDate || dataSource?.CreatedDate)
    );
    y += rowGap;

    fieldRow(col1X, "Complainant Name", dataSource.complainantName);
    fieldRow(col2X, "Email", dataSource.email);
    y += rowGap;

    fieldRow(col1X, "Mobile", dataSource.mobile);
    fieldRow(col2X, "Victim Gender", dataSource.victimGender);
    y += rowGap;

    fieldRow(col1X, "Aadhaar Number", dataSource.adharNumber);
    fieldRow(col2X, "Aadhaar File", submittedSnapshot?.aadhaarFileName || "-");
    y += rowGap;

    fieldRow(col1X, "Victim Age", dataSource.victimAge);
    fieldRow(col2X, "Relation With Victim", dataSource.relationWithVictim);
    y += rowGap;

    fieldRow(col1X, "Address", dataSource.address);
    fieldRow(col2X, "District", dataSource.district);
    y += rowGap;

    fieldRow(col1X, "State", dataSource.state);
    fieldRow(col2X, "Complaint Subject", dataSource.complaintHead);
    y += rowGap;

    doc.setTextColor(...labelColor);
    doc.text("Complaint Details:", col1X, y);
    y += 6;
    doc.setTextColor(...valueColor);
    const detailsLines = doc.splitTextToSize(dataSource.complaintDetails || "-", 180);
    doc.text(detailsLines, col1X, y);
    y += detailsLines.length * 5 + 6;

    doc.setTextColor(...labelColor);
    doc.text("Social Media Links:", col1X, y);
    doc.setTextColor(...valueColor);
    const social = socialSource.map((s) => s.link).filter(Boolean).join(", ") || "-";
    const socialLines = doc.splitTextToSize(social, 180);
    doc.text(socialLines, col1X + 40, y);
    y += socialLines.length * 5 + 6;

    doc.setTextColor(...labelColor);
    doc.text("Files Count:", col1X, y);
    doc.setTextColor(...valueColor);
    doc.text(String(filesCountSource), col1X + 30, y);

    // Footer is part of the letterhead background.

    doc.save("Online_Complaint_Details.pdf");
  };

  const normalizeState = (value) =>
    String(value || "")
      .toLowerCase()
      .replace(/and/g, "")
      .replace(/&/g, "")
      .replace(/[^a-z]/g, "");

  const getDistrictsForState = (state) => {
    const key = normalizeState(state);
    if (!key) return null;

    if (key === normalizeState("Dadra and Nagar Haveli and Daman & Diu")) {
      const a = districtsByState[normalizeState("Dadra and Nagar Haveli (UT)")] || [];
      const b = districtsByState[normalizeState("Daman and Diu (UT)")] || [];
      return Array.from(new Set([...a, ...b]));
    }

    if (key === normalizeState("Jammu & Kashmir")) {
      return (
        districtsByState[normalizeState("Jammu and Kashmir (UT)")] ||
        districtsByState[normalizeState("Jammu and Kashmir")] ||
        null
      );
    }

    if (key === normalizeState("Ladakh")) {
      return (
        districtsByState[normalizeState("Ladakh (UT)")] ||
        districtsByState[normalizeState("Ladakh")] ||
        null
      );
    }

    return districtsByState[key] || null;
  };

  // 🔹 Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.oathAccepted) {
      alert("Please accept the oath/undertaking before submitting.");
      return;
    }

    if (formData.captcha !== generatedCaptcha) {
      alert("Captcha does not match!");
      generateCaptcha();
      setFormData({ ...formData, captcha: "" });
      return;
    }

    const socialLinkValue = socialLinks.map((l) => l.link).filter(Boolean).join(",");


    const submissionData = new FormData();

submissionData.append("ComplainantName", formData.complainantName);
submissionData.append("Email", formData.email);
submissionData.append("Mobile", formData.mobile);
submissionData.append("AadharNumber", formData.adharNumber);
submissionData.append("VictimGender", formData.victimGender);
submissionData.append("VictimAge", Number(formData.victimAge));
submissionData.append("RelationWithVictim", formData.relationWithVictim);
submissionData.append("State", formData.state);
submissionData.append("District", formData.district);
submissionData.append("Address", formData.address);
submissionData.append("ComplaintHead", formData.complaintHead);
submissionData.append("ComplaintDetails", formData.complaintDetails);
    submissionData.append("CaseRegistered", formData.caseRegistered);
    submissionData.append("CasePending", formData.casePending);

submissionData.append("SocialMediaLink", socialLinkValue);
submissionData.append("OathAccepted", formData.oathAccepted ? "true" : "false");

    if (aadhaarFile) {
      submissionData.append("AadharCardFile", aadhaarFile);
    }

    filesList.forEach(file => {
      if (file) submissionData.append("Files", file);
    });

    try {
      const result = await createComplaint(submissionData);
      const appId =
        result?.applicationUniqueID ||
        result?.ApplicationUniqueID ||
        result?.ApplicationUniqueId ||
        result?.complaintID ||
        result?.ComplaintID ||
        "";
      setApplicationId(appId);
      const createdDateValue =
        result?.createdDate ||
        result?.CreatedDate ||
        result?.createdAt ||
        result?.CreatedAt ||
        new Date().toISOString();
      setSubmittedAt(createdDateValue);
      setSubmittedSnapshot({
        ...formData,
        socialLinks,
        aadhaarFileName: aadhaarFile?.name || "",
        createdDate: createdDateValue,
        filesCount: filesList.filter(Boolean).length,
      });
      setStatus(appId ? `✅ Complaint submitted successfully! ID: ${appId}` : "✅ Complaint submitted successfully!");
      setIsSubmitted(true); // ✅ enable PDF button
      setFormData({
        complainantName: "",
        email: "",
        mobile: "",
        adharNumber: "",
        victimGender: "",
        victimAge: "",
        relationWithVictim: "",
        state: "",
        district: "",
        address: "",
        complaintHead: "",
        complaintDetails: "",
        caseRegistered: "No",
        casePending: "No",
        oathAccepted: false,
        captcha: "",
      });
      setSocialLinks([]);
      setFilesList([]);
      setAadhaarFile(null);
      generateCaptcha();
    } catch (error) {
      console.error(error);
      setStatus("❌ Failed to submit complaint.");
    }
  };

  return (
    <div>
      <PageHeader title="Register Complaint" breadcrumbs={breadcrumbs} />
      <div className="container-xxl py-5">
        <div className="container">
          {status && (
            <div className="alert alert-info text-center fw-semibold">
              {status}
            </div>
          )}

          <div className="p-4 rounded shadow-sm bg-white">
            <form onSubmit={handleSubmit}>
              {/* Complainant Info */}
              <div className="row g-3 mb-3">
                <div className="col-md-4">
                  <div className="form-floating">
                    <input type="text" className="form-control" id="complainantName" placeholder="Complainant Name" value={formData.complainantName} onChange={handleChange} required />
                    <label> Name *</label>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-floating">
                    <input
                      type="email"
                      className={`form-control ${formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email) ? "is-invalid" : ""}`}
                      id="email"
                      placeholder="Email"
                      value={formData.email}
                      onChange={handleChange}
                      
                    />
                    <label>Email </label>

                    {/* Validation message */}
                    {formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email) && (
                      <div className="invalid-feedback" style={{ display: "block" }}>
                        Please enter a valid email address.
                      </div>
                    )}
                  </div>
                </div>

                <div className="col-md-4">
                  <div className="form-floating">
                    <input
                      type="tel"
                      className="form-control"
                      id="mobile"
                      placeholder="Mobile"
                      value={formData.mobile}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Allow only digits and limit to 10 characters
                        if (/^\d{0,10}$/.test(value)) {
                          handleChange(e);
                        }
                      }}
                      pattern="[0-9]{10}"
                      maxLength="10"
                      required
                    />
                    <label>Mobile *</label>
                  </div>
                </div>
                  </div>

              {/* Victim Info */}
              <div className="row g-3 mb-3">
                <div className="col-md-3">
                  <div className="form-floating">
                    <input
                      type="text"
                      className="form-control"
                      id="adharNumber"
                      placeholder="Aadhaar Number"
                      value={formData.adharNumber}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (/^\d{0,12}$/.test(value)) {
                          handleChange(e);
                        }
                      }}
                      maxLength="12"
                      
                    />
                    <label>Aadhaar Number </label>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="form-floating">
                    <select
                      className="form-select"
                      id="victimGender"
                      value={formData.victimGender}
                      onChange={handleChange}
                      required
                    >
                      <option value="">- Select -</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Trans">Transgender</option>
                      <option value="Other">Other</option>
                      <option value="NA">NA</option>
                    </select>
                    <label>Victim Gender *</label>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="form-floating">
                    <input
                      type="number"
                      className="form-control"
                      id="victimAge"
                      placeholder="Victim Age"
                      value={formData.victimAge}
                      onChange={handleChange}
                      required
                      min={1}
                      max={18}       // minimum value
                    />
                    <label><label>Victim Age (1-18) *</label></label>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="form-floating">
                    <select className="form-select" id="relationWithVictim" value={formData.relationWithVictim} onChange={handleChange} required>
                      <option value="">- Select -</option>
                      <option value="Self">Self</option>
                      <option value="Parent">Parent</option>
                      <option value="Guardian">Guardian</option>
                      <option value="Mother">Mother</option>
                      <option value="Father">Father</option>
                      <option value="Brother">Brother</option>
                      <option value="Sister">Sister</option>
                      <option value="Guardians">Guardians</option>
                      <option value="Other">Other</option>
                    </select>
                    <label>Relation with Victim *</label>
                  </div>
                </div>
              </div>

              {/* 🔹 Address Info */}
              <div className="row g-3 mb-3">
                <div className="col-md-4">
                  <div className="form-floating">
                    <select
                      className="form-select"
                      id="state"
                      value={formData.state}
                      onChange={handleChange}
                      required
                    >
                      <option value="">- Select -</option>
                      <option>Andhra Pradesh</option>
                      <option>Arunachal Pradesh</option>
                      <option>Assam</option>
                      <option>Bihar</option>
                      <option>Chhattisgarh</option>
                      <option>Goa</option>
                      <option>Gujarat</option>
                      <option>Haryana</option>
                      <option>Himachal Pradesh</option>
                      <option>Jharkhand</option>
                      <option>Karnataka</option>
                      <option>Kerala</option>
                      <option>Madhya Pradesh</option>
                      <option>Maharashtra</option>
                      <option>Manipur</option>
                      <option>Meghalaya</option>
                      <option>Mizoram</option>
                      <option>Nagaland</option>
                      <option>Odisha</option>
                      <option>Punjab</option>
                      <option>Rajasthan</option>
                      <option>Sikkim</option>
                      <option>Tamil Nadu</option>
                      <option>Telangana</option>
                      <option>Tripura</option>
                      <option>Uttar Pradesh</option>
                      <option>Uttarakhand</option>
                      <option>West Bengal</option>
                      <option>Andaman and Nicobar Islands</option>
                      <option>Chandigarh</option>
                      <option>Dadra and Nagar Haveli and Daman & Diu</option>
                      <option>Delhi</option>
                      <option>Jammu & Kashmir</option>
                      <option>Ladakh</option>
                      <option>Lakshadweep</option>
                      <option>Puducherry</option>
                    </select>
                    <label>State *</label>
                  </div>

                </div>
                <div className="col-md-4">
                  <div className="form-floating">
                    {getDistrictsForState(formData.state) ? (
                      <select
                        className="form-select"
                        id="district"
                        value={formData.district}
                        onChange={handleChange}
                        required
                      >
                        <option value="">- Select -</option>
                        {getDistrictsForState(formData.state).map((d) => (
                          <option key={d} value={d}>{d}</option>
                        ))}
                      </select>
                    ) : (
                      <input type="text" className="form-control" id="district" placeholder="District" value={formData.district} onChange={handleChange} required />
                    )}
                    <label>District *</label>
                  </div>
                  {districtsError && (
                    <div className="small text-danger mt-1">
                      {districtsError}
                    </div>
                  )}
                </div>
                
                 <div className="col-md-4">
                  <div className="form-floating">
                    <input type="text" className="form-control" id="address" placeholder="Address" value={formData.address} onChange={handleChange} required />
                    <label>Address *</label>
                  </div>
                </div>
               
              </div>

              {/* 🔹 Complaint Info */}
              <div className="mb-3">
                <div className="form-floating">
                  <input type="text" className="form-control" id="complaintHead" placeholder="Complaint Head" value={formData.complaintHead} onChange={handleChange} required />
                  <label>Complaint Subject *</label>
                </div>
              </div>
              <div className="mb-3">
                <div className="form-floating">
                  <textarea className="form-control" id="complaintDetails" placeholder="Complaint Details" value={formData.complaintDetails} onChange={handleChange} style={{ height: 100 }} required></textarea>
                  <label>Complaint Details *</label>
                </div>
              </div>


   {/* 🔹 Social Links */}
              <div className="mb-3 shadow rounded p-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <label className="fw-bold">Link(s) or PDF</label>
                  <button type="button" className="btn btn-primary btn-sm" onClick={addSocialLink}>Add Link</button>
                </div>
                <small className="text-muted d-block mb-2">
                  Provide any social media links if you have for evidence.
                </small>
                {socialLinks.map((item, index) => (
                  <div key={index} className="d-flex mb-2">
                    <input type="text" className="form-control me-2" placeholder="Link" value={item.link} onChange={(e) => handleSocialLinkChange(index, e.target.value)} />
                    <button type="button" className="btn btn-danger btn-sm" onClick={() => removeSocialLink(index)}>Remove</button>
                  </div>
                ))}
              </div>

              <div className="mb-4 shadow rounded p-4">
                <label className="fw-bold d-block mb-2">Aadhaar Card Photo/PDF </label>
                <small className="text-muted d-block mb-2">
                  Only allowed pdf, jpg, jpeg, png. Maximum file size should be 2MB.
                </small>
                <input
                  type="file"
                  className="form-control"
                  onChange={handleAadhaarFileChange}
                  accept=".pdf,.jpg,.jpeg,.png"
                  
                />
                {aadhaarFile ? (
                  <small className="text-success d-block mt-2">{aadhaarFile.name}</small>
                ) : null}
              </div>

              {/* 🔹 File Uploads */}
              <div className="mb-4 shadow rounded p-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <label className="fw-bold">Upload File(s)</label>
                  <button type="button" className="btn btn-primary btn-sm" onClick={addFileInput}>Add File </button>
                </div>
                <small className="text-muted d-block mb-2">
                  Only allowed pdf, jpg, jpeg, png, mp4, mpeg, mp3, wav With maximum file size should be 20MB.
                </small>
                {filesList.map((file, index) => (
                  <div key={index} className="d-flex mb-2">
                    <input type="file" className="form-control me-2" onChange={(e) => handleFileChange(index, e)} accept=".pdf,.jpg,.jpeg,.png,.mp4,.mpeg,.mp3,.wav" />
                    <button type="button" className="btn btn-danger btn-sm" onClick={() => removeFileInput(index)}>Remove</button>
                  </div>
                ))}
              </div>

             


              {/* 🔹 Captcha */}
              {/* 🔹 Modern Captcha */}
              <div className="form-check mb-4">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="oathAccepted"
                  checked={formData.oathAccepted}
                  onChange={handleChange}
                  required
                />
                <label className="form-check-label" htmlFor="oathAccepted">
                  I declare that the information provided is true and I accept this oath.
                </label>
              </div>
              <div className="row mb-3 p-4">
                <div className="col-md-12">
                  <label className="form-label fw-semibold">Captcha *</label>
                  <div className="d-flex align-items-center gap-2">

                    {/* Captcha Box */}
                    <div
                      className="d-flex justify-content-center align-items-center bg-white border rounded shadow-sm"
                      style={{
                        width: 130,
                        height: 45,
                        letterSpacing: 4,
                        fontWeight: "bold",
                        fontSize: "1.2rem",
                        userSelect: "none",
                      }}
                    >
                      {generatedCaptcha}
                    </div>

                    {/* Refresh Button (icon style) */}
                    <button
                      type="button"
                      className="btn btn-outline-secondary d-flex align-items-center justify-content-center"
                      style={{ width: 45, height: 45, padding: 0 }}
                      onClick={generateCaptcha}
                      title="Refresh Captcha"
                    >
                      <i className="bi bi-arrow-clockwise"></i>
                    </button>

                    {/* Captcha Input */}
                    <input
                      type="text"
                      className="form-control"
                      id="captcha"
                      placeholder="Enter Captcha"
                      value={formData.captcha}
                      onChange={handleChange}
                      required
                      style={{ maxWidth: 200 }}
                    />
                  </div>
                  <small className="text-muted">Enter the characters shown in the box.</small>
                </div>
              </div>
           

              {/* 🔹 Submit */}
              <div className="text-center">
                <button type="submit" className="btn btn-danger rounded-pill px-4">
                  Submit Complaint
                </button>

                {isSubmitted && (
                  <button
                    type="button"
                    className="btn btn-success rounded-pill px-4 ms-2"
                    onClick={generatePDF}
                  >
                    Download PDF
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}





